import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { TierResponse } from '@models/tier';
import { useTranslation } from 'react-i18next';

interface Props {
    data: TierResponse;
}

function TierGrade({ data }:Props) {
    const { t } = useTranslation('tier');
    const {
        tier, tierStatus, nextTier, tierStatusRemaining, accumulationType,
    } = data;
    const priceUnit = usePriceUnit();
    return (
        <Row fullWidth justify='space-between' css={css`height: 32px;`}>
            {tier.displayType === 'ICON' ? (
                <Row spacing={2}>
                    <Image src={tier.iconFileInfo?.url}
                        alt={t('tierInfo.iconAlt')}
                        width={24}
                        height={24}/>
                    <Text fontWeight='bold'>
                        {tier.name}
                    </Text>
                </Row>
            ) : (
                <TierBadge textColor={tier.textColor}
                    backgroundColor={tier.backgroundColor}>
                    {tier.name}
                </TierBadge>
            )}
            {(tierStatus === 'ACHIEVE' && nextTier) && (
                <Text color={colors.gray6} size='body-long'>
                    {t('tierInfo.achievePrefix', { name: nextTier?.name })}&nbsp;
                    <b>
                        {tierStatusRemaining.toLocaleString()}{accumulationType === 'STAMP' ? t('tierInfo.stampUnit') : priceUnit}
                    </b>
                    {t('tierInfo.remainingSuffix')}
                </Text>
            )}
            {tierStatus === 'MAINTAIN' && (
                <Text size='body-long' color={tier.backgroundColor}>
                    {t('tierInfo.maintainPrefix', { name: tier.name })}&nbsp;
                    <b>
                        {tierStatusRemaining.toLocaleString()}{accumulationType === 'STAMP' ? t('tierInfo.stampUnit') : priceUnit}
                    </b>
                    {t('tierInfo.remainingSuffix')}
                </Text>
            )}
            {tierStatus === 'CONFIRMED' && (
                <Row spacing={1}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M4.99762 0.474455C5.3884 -0.158165 6.30838 -0.158165 6.69916 0.474455L7.96993 2.53166C8.10801 2.7552 8.32853 2.91541 8.58379 2.97766L10.933 3.55052C11.6554 3.72668 11.9397 4.60164 11.4588 5.16879L9.89498 7.01307C9.72505 7.21347 9.64082 7.4727 9.6605 7.73471L9.84163 10.146C9.89732 10.8875 9.15304 11.4282 8.46505 11.1461L6.22778 10.2287C5.98467 10.1291 5.7121 10.1291 5.469 10.2287L3.23173 11.1461C2.54374 11.4282 1.79945 10.8875 1.85515 10.146L2.03627 7.73471C2.05595 7.4727 1.97173 7.21347 1.8018 7.01307L0.237967 5.16879C-0.242932 4.60164 0.0413588 3.72668 0.763773 3.55052L3.11299 2.97766C3.36825 2.91541 3.58876 2.7552 3.72685 2.53166L4.99762 0.474455Z"
                            fill={nextTier?.backgroundColor}/>
                    </svg>
                    <Text size='body-long' fontWeight='medium' color={nextTier?.backgroundColor}>
                        {t('tierInfo.confirmed', { name: nextTier?.name })}
                    </Text>
                </Row>
            )}
        </Row>
    );
}

const TierBadge = styled.div<{
    backgroundColor?: string;
    textColor?: string;
}>`
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 14px;
    font-weight: bold;
    background-color: ${({ backgroundColor }) => backgroundColor};
    color: ${({ textColor }) => textColor};
`;

export default TierGrade;
